import {
  ArrowForward,
  DeleteOutline,
  Edit,
  InfoOutlined,
} from '@mui/icons-material';
import DataGrid from 'components/DataGrid';
import IconButton from 'components/IconButton';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CardBody, CardHeader } from 'reactstrap';

import {
  formatKPIValue,
  getMetricOutcome,
  getSectionStatus,
  isMetricAvailable,
  KPIOutcome,
} from './helper';
import KPISectionIcon from './KPISectionIcon';
import styles from './styles.module.css';
import { KPIMetric, KPISection } from './types';

interface SectionCardProps {
  section: KPISection;
  onDeleteCustomKPI?: (section: KPISection) => void;
  onEditCustomKPI?: (section: KPISection) => void;
}

const outcomeDetails: Record<KPIOutcome, { label: string; className: string }> =
  {
    GOOD: { label: 'Improving', className: styles.trafficGood },
    BAD: { label: 'Worsening', className: styles.trafficBad },
    NEUTRAL: { label: 'Stable', className: styles.trafficNeutral },
  };

const getMetricTitle = (metric: KPIMetric, outcome: KPIOutcome) => {
  const outcomeLabel = outcomeDetails[outcome].label;
  return `${outcomeLabel}. Previous: ${formatKPIValue(
    metric.previous_value,
    metric.unit,
  )}`;
};

const SectionCard = ({
  section,
  onDeleteCustomKPI,
  onEditCustomKPI,
}: SectionCardProps) => {
  const location = useLocation();
  // Keep detail navigation inside the active layout so client users are not
  // redirected into admin routes when opening a KPI from the shared dashboard.
  const layoutPath = location.pathname.startsWith('/client')
    ? '/client'
    : '/admin';
  const sectionStatus = getSectionStatus(section.metrics);
  const availableMetrics = section.metrics.filter(isMetricAvailable);
  const sources = Array.from(
    new Set(availableMetrics.map((metric) => metric.source)),
  );
  const sourceMessage =
    availableMetrics.length === 0
      ? 'No KPI data available'
      : sources.length === 1 && sources[0] !== 'SP360'
        ? sources[0] === 'MANUAL'
          ? 'Manually updated'
          : `Source: ${sources[0]}`
        : null;
  const dataGridRows = section.metrics.map((metric) => ({
    key: metric.key,
    label: metric.label,
    metric,
  }));

  return (
    <article className={styles.sectionCard}>
      <CardHeader className={styles.sectionCardHeader}>
        <div className={styles.sectionIdentity}>
          <KPISectionIcon sectionKey={section.key} />
          <h4>{section.label}</h4>
        </div>
        <span
          className={`${styles.statusBadge} ${styles[`status${sectionStatus.replace(' ', '')}`]}`}
        >
          {sectionStatus}
        </span>
      </CardHeader>
      <CardBody className={styles.sectionDataGridBody}>
        {/* Keep KPI-specific card presentation local while reusing the existing
            DataGrid without extending shared dashboard component contracts. */}
        <div className="overflow-auto" style={{ overflowX: 'hidden' }}>
          <DataGrid
            data={dataGridRows}
            config={{
              rowIdKey: 'key',
              columns: [
                {
                  key: 'label',
                  label: 'KPI',
                  headerTooltip: 'KPI included in this dashboard area',
                },
                {
                  key: 'previous',
                  label: 'Previous',
                  width: '76px',
                  overrideContents: (rowData) => {
                    const metric = rowData.metric as KPIMetric;
                    return formatKPIValue(metric.previous_value, metric.unit);
                  },
                },
                {
                  key: 'current',
                  label: 'Current',
                  width: '76px',
                  rowCellStyle: { fontWeight: 600 },
                  overrideContents: (rowData) => {
                    const metric = rowData.metric as KPIMetric;
                    return formatKPIValue(metric.value, metric.unit);
                  },
                },
                {
                  key: 'status',
                  label: 'Status',
                  width: '54px',
                  overrideContents: (rowData) => {
                    const metric = rowData.metric as KPIMetric;
                    const outcome = getMetricOutcome(metric);
                    const outcomeDetail = outcomeDetails[outcome];
                    const metricTitle = getMetricTitle(metric, outcome);
                    return (
                      <span
                        className={`${styles.trafficLight} ${outcomeDetail.className}`}
                        title={metricTitle}
                        aria-label={`${metric.label}: ${metricTitle}`}
                      />
                    );
                  },
                },
              ],
            }}
          />
        </div>
      </CardBody>
      {sourceMessage && (
        <div className={styles.sourceMessage}>
          <InfoOutlined fontSize="small" />
          <span>{sourceMessage}</span>
        </div>
      )}
      <footer className={styles.sectionCardFooter}>
        <Link
          className={styles.viewDetailsButton}
          to={`${layoutPath}/kpi-dashboard/${section.key}`}
        >
          View Details
          <ArrowForward fontSize="small" />
        </Link>
        {section.is_custom && onEditCustomKPI && onDeleteCustomKPI && (
          <div className={styles.sectionCardActions}>
            <IconButton
              id={`edit-custom-kpi-${section.id}`}
              iconElement={<Edit fontSize="small" />}
              size="sm"
              tooltip="Edit custom KPI"
              onClick={() => onEditCustomKPI(section)}
            />
            <IconButton
              id={`delete-custom-kpi-${section.id}`}
              iconElement={<DeleteOutline fontSize="small" />}
              size="sm"
              tooltip="Delete custom KPI"
              onClick={() => onDeleteCustomKPI(section)}
            />
          </div>
        )}
      </footer>
    </article>
  );
};

export default SectionCard;
